import { datePl, KSEF_STATUS_LABELS, type SalesInvoiceDetailDto, type SalesInvoiceRelatedDto } from '@bqcrm/shared';
import { ActionIcon, Alert, Group, Stack, Table, Text, Tooltip } from '@mantine/core';
import { IconFileTypePdf, IconRefresh, IconSend } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ask } from '../confirm';
import { notifyError, notifyOk } from '../notify';
import { InvoicePayStatusBadge, TestModeBadge } from './Badges';
import { EMAIL_LABEL } from './InvoicesPanel';
import { Money } from './Money';

/**
 * „Wszystkie dokumenty …” źródła bez własnego panelu (zgłoszenie serwisowe) — ten sam układ co dokumenty umowy:
 * PDF, wysyłka i odświeżenie z Fakturowni przez wspólne trasy faktur sprzedaży.
 */
export function RelatedInvoiceDocs({ data, onChanged }: { data: SalesInvoiceRelatedDto; onChanged: () => void }) {
  const qc = useQueryClient();
  const done = (id: string, d: SalesInvoiceDetailDto) => {
    qc.setQueryData(['sales-invoice', id], d);
    onChanged();
  };
  const send = useMutation({
    mutationFn: (id: string) => api<SalesInvoiceDetailDto>(`/finance/sales-invoices/${id}/send`, { method: 'POST' }),
    onSuccess: (d, id) => (done(id, d), notifyOk(d.emailTo ? `Wysłano fakturę do klienta (${d.emailTo})` : 'Wysłano fakturę do klienta')),
    onError: notifyError,
  });
  const refresh = useMutation({
    mutationFn: (id: string) => api<SalesInvoiceDetailDto>(`/finance/sales-invoices/${id}/refresh`, { method: 'POST' }),
    onSuccess: (d, id) => (done(id, d), notifyOk('Pobrano stan z Fakturowni')),
    onError: notifyError,
  });

  return (
    <Stack gap="md">
      <Text fw={700} size="sm">
        {data.title}
      </Text>
      {data.mode === 'test' && (
        <Alert color="yellow" variant="light" py="xs">
          Konto testowe Fakturowni — faktury nie są prawdziwymi dokumentami księgowymi.
        </Alert>
      )}
      <Table.ScrollContainer minWidth={620}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Dokument</Table.Th>
              <Table.Th>Data</Table.Th>
              <Table.Th ta="right">Brutto</Table.Th>
              <Table.Th>Stan</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.documents.map((i) => (
              <Table.Tr key={i.id}>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {i.number ?? 'Faktura VAT'}
                    {i.mode === 'test' && <TestModeBadge ml={6} />}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {i.kindLabel}
                  </Text>
                  {i.emailStatus && EMAIL_LABEL[i.emailStatus] && (
                    <Text size="xs" c={EMAIL_LABEL[i.emailStatus]!.color}>
                      e-mail: {EMAIL_LABEL[i.emailStatus]!.text}
                      {i.emailTo ? ` (${i.emailTo})` : ''}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{i.issueDate ? datePl(i.issueDate) : '—'}</Text>
                  {i.paymentTo && (
                    <Text size="xs" c="dimmed">
                      termin {datePl(i.paymentTo)}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm">
                    <Money gr={i.grossGr} />
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    {i.extStatus && <InvoicePayStatusBadge extStatus={i.extStatus} />}
                    {i.ksefStatus && i.ksefStatus !== 'not_applicable' && (
                      <Text size="xs" c={i.ksefStatus.includes('error') ? 'red' : 'dimmed'}>
                        {KSEF_STATUS_LABELS[i.ksefStatus] ?? `KSeF: ${i.ksefStatus}`}
                        {i.ksefNumber ? ` · ${i.ksefNumber}` : ''}
                      </Text>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap" justify="flex-end">
                    {i.can.pdf && (
                      <Tooltip label="PDF z Fakturowni">
                        <ActionIcon variant="subtle" color="gray" component="a" href={`/api/finance/sales-invoices/${i.id}/pdf`} target="_blank" rel="noopener" aria-label="PDF">
                          <IconFileTypePdf size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {i.can.send && (
                      <Tooltip label={i.emailStatus === 'wyslano' ? `Wysłano ${i.emailTo ?? ''} — wyślij ponownie` : 'Wyślij do klienta'}>
                        <ActionIcon
                          variant="subtle"
                          color={i.emailStatus === 'wyslano' ? 'green' : 'gray'}
                          loading={send.isPending && send.variables === i.id}
                          onClick={async () => (await ask(i.emailTo ? `Faktura ${i.number ?? ''} pójdzie na ${i.emailTo}.` : 'Faktura pójdzie na adres klienta.', { title: 'Wysłać fakturę do klienta?', confirm: 'Wyślij' })) && send.mutate(i.id)}
                          aria-label="Wyślij"
                        >
                          <IconSend size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {i.can.refresh && (
                      <Tooltip label="Odśwież stan (opłacenie, KSeF)">
                        <ActionIcon variant="subtle" color="gray" loading={refresh.isPending && refresh.variables === i.id} onClick={() => refresh.mutate(i.id)} aria-label="Odśwież">
                          <IconRefresh size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
}
